import React, { RefObject, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { StyleSheet, StyleProp, ViewStyle, PanResponderGestureState, GestureResponderEvent, Platform, TextStyle } from 'react-native';
import ReactNativeModal, { OnOrientationChange, OnSwipeCompleteParams, PresentationStyle, Direction } from 'react-native-modal';
import { MB_ModalUtilChildWrapper, MB_ModalUtilChildWrapperProps } from './MB_ModalUtilChildWrapper';
import { isMobileApp } from '@/utils/general/device-info';
import { MB_ModalAnimationProps, MB_ModalAnimations } from './typesAndInterfaces';
import { MB_ButtonProps } from '@/components/general/button';

// TODO: Possibly switch to that other library that Anas showed

interface MB_ModalRef {
  hideModal: () => void;
}

const MB_ModalSetDefaultProps = (props: MB_ModalDefaultProps) => {
  defaultProps = props;
};

let defaultProps: MB_ModalDefaultProps | undefined;

interface MB_ModalProps extends MB_ModalUtilChildWrapperProps, MB_ModalAnimationProps {
  /**
   * Modal reference object.
   */
  MB_Ref?: RefObject<MB_ModalRef | null>;

  /**
   * Modal visibility.
   */
  isVisible: boolean;

  /**
   * Style for the modal. Note that this gets applied to the `react-native-modal`
   * and should almost never be used.
   */
  modalStyle?: StyleProp<ViewStyle>;

  /**
   * When set to true, children components are not removed when the modal closes.
   */
  keepChildrenAfterDismiss?: boolean;

  /**
   * Allows swipe events to propagate to children components, for example,
   * propagate the swipe event to a ScrollView component inside the modal.
   *
   * Default: true
   */
  propogateSwipe?: boolean | ((event: GestureResponderEvent, gestureState: PanResponderGestureState) => boolean);

  /**
   * When set to true, move the modal up if the keyboard is open.
   *
   * Default: false
   */
  avoidKeyboard?: boolean;

  /**
   * When set to true, will cover the entire screen wherever the modal is
   * mounted in the component hierarchy.
   *
   * Default: true
   */
  coverScreen?: boolean;

  /**
   * Callback called when the modal is dismissed. This should be the default way of listening for modal dismiss
   * since it is called only when modal hide animation is finished.
   */
  onDismiss?: (isVisible: false) => void;

  /**
   * Callback called once the modal has been shown.
   */
  onShow?: () => void;

  /**
   * Callback called when the modal is completely visible.
   */
  onModalShow?: () => void;

  /**
   * Callback before the modal show animation begins.
   */
  onModalWillShow?: () => void;

  /**
   * Callback called when the modal is completely hidden.
   */
  onModalHide?: () => void;

  /**
   * Callback called before the modal hide animation begins.
   */
  onModalWillHide?: () => void;

  /**
   * Callback called when the back button is pressed on Android.
   */
  onBackButtonPress?: () => void;

  /**
   * Callback called when the swipe action has started.
   */
  onSwipeStart?: (gestureState: PanResponderGestureState) => void;

  /**
   * Callback called on each swipe event.
   */
  onSwipeMove?: (percentageShown: number, gestureState: PanResponderGestureState) => void;

  /**
   * Callback called when the `swipeThreshold` has been reached.
   */
  onSwipeComplete?: (params: OnSwipeCompleteParams, gestureState: PanResponderGestureState) => void;

  /**
   * Callback called when the `swipeThreshold` has not been reached.
   */
  onSwipeCancel?: (gestureState: PanResponderGestureState) => void;

  /**
   * When set to true, animations should use the native driver.
   */
  useNativeDriver?: boolean;

  /**
   * When set to true, hides the modal's content until the animations
   * complete. This improves performance.
   */
  hideModalContentWhileAnimating?: boolean;

  /**
   * Controls how the modal appears on iOS.
   *
   *  - `fullScreen`: The modal covers the screen. This is the default.
   *  - `pageSheet`: The modal covers portrait-width view centered (only on larger devices).
   *  - `formSheet`: The modal covers narrow-width view centered (only on larger devices).
   *  - `overFullScreen`: The modal covers the screen, but allows transparency.
   *
   * See https://developer.apple.com/documentation/uikit/uimodalpresentationstyle
   */
  presentationStyle?: PresentationStyle;

  /**
   * Callback called when the iOS device orientation changes between portrait
   * and landscape. This callback is also called on initial render, regardless
   * of the current orientation.
   */
  onOrientationChange?: OnOrientationChange;

  /**
   * The direction(s) the modal can be swiped.
   */
  swipeDirection?: Direction | Array<Direction>;

  /**
   * Callback called when a keyboard key is released on web.
   * Note that you will not get a callback unless `props.isVisible` is `true`.
   */
  onKeyUp?: (event: React.KeyboardEvent) => void;

  /**
   * Callback called when a keyboard key is pressed on web.
   * Note that you will not get a callback unless `props.isVisible` is `true`.
   */
  onKeyDown?: (event: React.KeyboardEvent) => void;
  titleWrapperStyle?: StyleProp<ViewStyle>;
}

let globalChildrenArray: React.JSX.Element[] = [];

/**
 * This function allows you to set global children that will be rendered in all modals. Very helpful for mounting
 * things like MB_PopUp or MB_Toast with this since we need those mounted every time a modal is mounted so that we can actually
 * show the MB_PopUp or MB_Toast inside the modal when modal is visible.
 * @param globalChildren array of children to be rendered in all modals.
 */
const MB_ModalAttachGlobalChildren = (globalChildren: React.JSX.Element[]) => {
  globalChildrenArray = globalChildren;
};

const MB_ModalInternal = (props: MB_ModalProps) => {
  const reactNativeModalRef = useRef<ReactNativeModal>(null);
  const [dismissInProgress, setDismissInProgress] = useState(false);

  useImperativeHandle(props.MB_Ref, () => ({
    hideModal: () => {
      setDismissInProgress(true);
      reactNativeModalRef.current?.close();
    },
  }));

  const keyboardListeners = useMemo(() => {
    if (isMobileApp || !props.isVisible) {
      return {};
    }
    return { onKeyDown: props.onKeyDown, onKeyUp: props.onKeyUp };
  }, [props.isVisible, props.onKeyDown, props.onKeyUp]);

  return (
    <ReactNativeModal
      ref={reactNativeModalRef}
      animationIn={props.animationIn}
      animationInTiming={props.animationInTiming ?? 300}
      animationOut={props.animationOut}
      animationOutTiming={props.animationOutTiming}
      avoidKeyboard={props.avoidKeyboard ?? false}
      coverScreen={props.coverScreen ?? true}
      hasBackdrop={false}
      backdropColor={props.backdropColor ?? 'black'}
      backdropOpacity={props.backdropOpacity ?? 0.7}
      customBackdrop={props.customBackdrop}
      isVisible={dismissInProgress ? false : props.isVisible}
      onModalShow={props.onModalShow}
      onModalWillShow={props.onModalWillShow}
      onModalHide={() => {
        setDismissInProgress(false);
        props.onDismiss?.(false);
        props.onModalHide?.();
      }}
      onModalWillHide={props.onModalWillHide}
      onBackdropPress={props.onBackdropPress}
      onBackButtonPress={() => {
        if (props.onBackButtonPress) {
          props.onBackButtonPress();
          return;
        }

        setDismissInProgress(true);
        reactNativeModalRef.current?.close();
      }}
      onSwipeStart={props.onSwipeStart}
      onSwipeMove={props.onSwipeMove}
      onSwipeComplete={props.onSwipeComplete}
      onSwipeCancel={props.onSwipeCancel}
      // iOS modals were flickering when using native driver
      useNativeDriver={props.useNativeDriver ?? (Platform.OS === 'web' || Platform.OS === 'ios') ? false : true}
      useNativeDriverForBackdrop={props.useNativeDriver ?? Platform.OS === 'web' ? false : true}
      hideModalContentWhileAnimating={props.hideModalContentWhileAnimating ?? false}
      propagateSwipe={props.propogateSwipe ?? true}
      style={[styles.modalStyle, props.modalStyle]}
      onShow={props.onShow}
      presentationStyle={props.presentationStyle}
      onOrientationChange={props.onOrientationChange}
      swipeDirection={props.swipeDirection}
      pointerEvents={dismissInProgress || !props.isVisible ? 'none' : undefined}
      {...keyboardListeners}
    >
      <MB_ModalUtilChildWrapper
        simplified={props.simplified}
        withoutScrollView={props.withoutScrollView}
        children={props.children}
        childrenWrapperStyle={props.childrenWrapperStyle}
        onCloseButtonPressed={props.onCloseButtonPressed}
        hideCloseButton={props.hideCloseButton}
        closeButtonProps={props.closeButtonProps}
        closeButton={props.closeButton}
        closeButtonXProps={props.closeButtonXProps}
        disableBackdropBlur={props.disableBackdropBlur}
        customBackdrop={props.customBackdrop}
        backdropBlurIntensity={props.backdropBlurIntensity}
        backdropBlurTint={props.backdropBlurTint}
        closeOnBackdropPress={props.closeOnBackdropPress}
        onBackdropPress={props.onBackdropPress}
        backdropColor={props.backdropColor}
        backdropOpacity={props.backdropOpacity}
        title={props.title}
        titleStyle={props.titleStyle}
        titleCustomComponent={props.titleCustomComponent}
        scrollViewContentContainerStyle={props.scrollViewContentContainerStyle}
        withoutScrollViewContainer={props.withoutScrollViewContainer}
        onDismissInProgress={() => {
          setDismissInProgress(true);
          reactNativeModalRef.current?.close();
        }}
        keyboardAwareProps={props.keyboardAwareProps}
        keyboardShouldPersistTaps={props.keyboardShouldPersistTaps}
        titleWrapperStyle={props.titleWrapperStyle}
      />

      {globalChildrenArray.map(globalChild => globalChild)}
    </ReactNativeModal>
  );
};

interface MB_ModalDefaultProps {
  keyboardAwareProps?: {
    /**
     * Adds an extra offset to the keyboard. Useful if you want to stick elements
     * above the keyboard.
     */
    extraScrollHeight?: number;
  };

  /**
   * Style for the view that wraps the modal's children.
   */
  childrenWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Title style that is shown by default when using a not simplified modal.
   */
  titleStyle?: StyleProp<TextStyle>;

  /**
   * Button props for the default close button.
   */
  closeButtonProps?: MB_ButtonProps;

  /**
   * Controls the color and size for the close button's X symbol.
   */
  closeButtonXProps?: { size?: number; color?: string };
}

const MB_Modal = (props: MB_ModalProps) => {
  if (defaultProps) {
    const newProps: MB_ModalDefaultProps = {};
    Object.keys(defaultProps).forEach(key => {
      const stylePropsKey = key as keyof MB_ModalDefaultProps;

      if (stylePropsKey === 'closeButtonProps' || stylePropsKey === 'closeButtonXProps' || stylePropsKey === 'keyboardAwareProps') {
        newProps[stylePropsKey] = props[stylePropsKey] ? { ...defaultProps?.[stylePropsKey], ...props[stylePropsKey] } : defaultProps?.[stylePropsKey];
      } else {
        newProps[stylePropsKey] = props[stylePropsKey] ? [defaultProps?.[stylePropsKey], props[stylePropsKey]] : defaultProps?.[stylePropsKey];
      }
    });

    return <MB_ModalInternal {...props} {...newProps} />;
  }

  return <MB_ModalInternal {...props} />;
};

export { MB_Modal, MB_ModalAttachGlobalChildren, MB_ModalSetDefaultProps };
export type { MB_ModalRef, MB_ModalAnimations, MB_ModalProps };

const styles = StyleSheet.create({
  modalStyle: {
    margin: 0,
    flex: 1,
  },
});
